import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import moment from 'moment';
import PeriodFilter from './filters/PeriodFilter';
import DateFilter from './filters/DateFilter';
import AmountFilter from './filters/AmountFilter';
import SearchFilter from './filters/SearchFilter';
import Tag from '@/components/ui/Tag';
import Button from '@/components/ui/Button';

interface IProps {
  period: string | null;
  setPeriod: (period: string | null) => void;
  search: string;
  setSearch: (search: string) => void;
  amount: { min: number | null; max: number | null };
  setAmount: (amount: { min: number | null; max: number | null }) => void;
  dateRange: [Date | null, Date | null];
  setDateRange: (dateRange: [Date | null, Date | null]) => void;
  clearAllFilters: () => void;
}

const periodOptions = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'this-week' },
  { label: 'This month', value: 'this-month' },
  { label: 'Last 3 months', value: 'last-3-months' },
];

const TransactionsFilters: FC<IProps> = ({
  period,
  setPeriod,
  search,
  setSearch,
  amount,
  setAmount,
  dateRange,
  setDateRange,
  clearAllFilters,
}) => {
  const hasActiveFilters =
    !!period ||
    !!search ||
    amount.min !== null ||
    amount.max !== null ||
    dateRange[0] !== null;
  const periodLabel = periodOptions.find((p) => p.value === period)?.label;

  const handlePeriodApply = (p: string | null) => {
    setPeriod(p);
    if (p) {
      setDateRange([null, null]);
    }
  };

  const handleDateRangeApply = (dr: [Date | null, Date | null]) => {
    setDateRange(dr);
    if (dr[0]) {
      setPeriod(null);
    }
  };

  const dateRangeLabel = useMemo(() => {
    if (!dateRange[0]) return null;
    const start = moment(dateRange[0]).format('DD/MM/YYYY');

    if (!dateRange[1] || moment(dateRange[0]).isSame(dateRange[1], 'day')) {
      return start;
    }
    const end = moment(dateRange[1]).format('DD/MM/YYYY');

    return `${start} - ${end}`;
  }, [dateRange]);

  let amountLabel = '';
  const format = (n: number) =>
    `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

  if (amount.min !== null && amount.max !== null) {
    amountLabel = `${format(amount.min)} - ${format(amount.max)}`;
  } else if (amount.min !== null) {
    amountLabel = format(amount.min);
  } else if (amount.max !== null) {
    amountLabel = format(amount.max);
  }

  return (
    <Box>
      <Box className="grid grid-cols-5 gap-4 items-center mb-4">
        <PeriodFilter period={period} onApply={handlePeriodApply} />
        <DateFilter dateRange={dateRange} onApply={handleDateRangeApply} />
        <AmountFilter amount={amount} onApply={setAmount} />
        <div className="col-span-2">
          <SearchFilter search={search} setSearch={setSearch} />
        </div>
      </Box>
      {hasActiveFilters && (
        <div className="-mx-6 flex h-14 items-center justify-between border-b border-t border-grey-200 px-6 py-3">
          <div className="flex items-center gap-2">
            {periodLabel && (
              <Tag label={periodLabel} onRemove={() => setPeriod(null)} />
            )}
            {dateRangeLabel && (
              <Tag
                label={dateRangeLabel}
                onRemove={() => setDateRange([null, null])}
              />
            )}
            {amountLabel && (
              <Tag
                label={amountLabel}
                onRemove={() => setAmount({ min: null, max: null })}
              />
            )}
          </div>
          <Button
            label="Clear all"
            size="small"
            variant="critical"
            onClick={clearAllFilters}
          />
        </div>
      )}
    </Box>
  );
};

export default TransactionsFilters;
